import { cn } from "../lib/cn";

export interface LegalLinksProps {
  /** Where the documents are published — the landing site, without a trailing slash. */
  baseUrl: string;
  /** The language the reader is in; the landing site is prefixed by locale. */
  locale: string;
  /** Already-translated words, because this package carries no dictionary. */
  labels: { privacy: string; terms: string; version?: string };
  /** One sentence under the links: who the controller is and who processes for them. */
  notice?: string;
  className?: string;
  testId?: string;
}

/**
 * The two links every application owes the person using it (US-37.9, L-11).
 *
 * <p>Until this story none of the three applications had them. A colleague invited into a roastery
 * and a wholesale buyer signing into the portal never found out who was processing their data or on
 * what terms — RODO art. 13 says they are owed that, and the honest place for it is the screen they
 * are already on.
 *
 * <p>Links rather than copies (EP-37 decision 5): one set of documents on the landing site, three
 * applications pointing at it. Three copies of a privacy policy is three chances for one of them to
 * be out of date, and the one that is out of date is the one somebody will read.
 */
export function LegalLinks({ baseUrl, locale, labels, notice, className, testId }: LegalLinksProps) {
  const base = `${baseUrl.replace(/\/+$/, "")}/${locale}`;
  return (
    <div className={cn("flex flex-col gap-1 text-xs text-muted-foreground", className)} data-testid={testId}>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <a
          href={`${base}/privacy`}
          target="_blank"
          rel="noreferrer noopener"
          className="underline underline-offset-4 hover:text-foreground"
          data-testid={testId ? `${testId}-privacy` : undefined}
        >
          {labels.privacy}
        </a>
        <a
          href={`${base}/terms`}
          target="_blank"
          rel="noreferrer noopener"
          className="underline underline-offset-4 hover:text-foreground"
          data-testid={testId ? `${testId}-terms` : undefined}
        >
          {labels.terms}
        </a>
        {labels.version ? <span>{labels.version}</span> : null}
      </span>
      {notice ? <span>{notice}</span> : null}
    </div>
  );
}
